import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import type {
  EnvioEnCamino,
  EstadoInventario,
  InsumoDelCatalogo,
  MovimientoRegistrado,
  SaldoInventario,
  Ubicacion,
} from '@manejo-alcanza/contracts';
import {
  cantidad as formatoCantidad,
  corregirMovimiento,
  empacar,
  enviar,
  estadoInventario,
  fecha,
  producir,
  recibir,
  registrarMovimiento,
} from '../api';
import { Aviso, Card, Cargando, Error_ } from '../componentes/basicos';

/**
 * F9 — el inventario. **Diseñada para el celular**, como F4.
 *
 * Quien la usa está en la planta o en la bodega, de pie, con las manos
 * ocupadas: tarjetas en vez de tablas, objetivos grandes, y en pantalla ancha
 * las tarjetas se reparten en columnas — **la misma pantalla, no otra**.
 *
 * ## El saldo no se escribe: se calcula
 *
 * No hay ningún campo «existencias» que editar, y eso es a propósito
 * *(invariante 3)*. Lo que se registra son hechos, y el saldo sale de sumarlos.
 * **Y corregir no es editar**: el botón anota el movimiento contrario, con
 * motivo, y el original se queda.
 *
 * ## Los dos sitios no hacen lo mismo
 *
 * *(Pedro, 20/09/2026.)* Y por eso la pantalla no ofrece lo mismo en cada uno:
 *
 * ```
 * PLANTA     leche + insumos  →  queso a granel (kg)  →  envío
 *                                                           ↓  en tránsito
 * BODEGA     queso recibido  +  etiqueta + empaque  →  presentación vendible
 * ```
 *
 * En planta se produce y se envía. En Bogotá se **recibe** —de ahí que el botón
 * diga «Recibí queso» y no «Produje»— y se empaca, porque de planta el queso
 * sale sin porcionar. El backend rechaza igual lo que no corresponda: un
 * empaque en planta no es un botón mal puesto, es un hecho que no ocurrió.
 */
export function Inventario(): ReactNode {
  const [donde, setDonde] = useState<Ubicacion>('PLANTA');
  const [haciendo, setHaciendo] = useState<Accion | null>(null);

  const consulta = useQuery({
    queryKey: ['inventario', donde],
    queryFn: () => estadoInventario(donde),
  });

  if (consulta.isLoading) return <Cargando que="el inventario" />;
  if (consulta.isError) return <Error_>{(consulta.error as Error).message}</Error_>;

  const e = consulta.data!;
  const negativos = e.saldos.filter((s) => Number(s.cantidad) < 0);
  const enCamino = e.enviosEnCamino.filter((v) => v.desde !== donde);

  return (
    <div className="space-y-4 pb-6">
      <header>
        <h1 className="text-2xl font-semibold">Inventario</h1>
        <p className="mt-1 text-tenue">
          El saldo no se escribe: <strong>sale de sumar lo que pasó</strong>.
        </p>
      </header>

      <DondeEstoy
        donde={donde}
        alCambiar={(u) => {
          setDonde(u);
          setHaciendo(null);
        }}
      />

      {negativos.length > 0 && (
        <Aviso>
          <strong>
            {negativos.length} {negativos.length === 1 ? 'saldo' : 'saldos'} en negativo.
          </strong>{' '}
          Salió algo que nunca entró — probablemente falta registrar una producción o una
          recepción. No se bloquea nada: se avisa.
        </Aviso>
      )}

      {enCamino.length > 0 && (
        <Aviso>
          <strong>
            {enCamino.length} {enCamino.length === 1 ? 'envío' : 'envíos'} en camino.
          </strong>{' '}
          Salieron y nadie ha anotado que llegaron. Están abajo, en «Recibí queso».
        </Aviso>
      )}

      <Saldos saldos={e.saldos} insumos={e.insumos} />

      <Acciones
        estado={e}
        donde={donde}
        haciendo={haciendo}
        alElegir={setHaciendo}
        alTerminar={() => setHaciendo(null)}
      />

      <Libro movimientos={e.movimientos} />
    </div>
  );
}

type Accion = 'producir' | 'recibir' | 'empacar' | 'enviar' | 'insumo';

// ---------------------------------------------------------------------------

/** Dos sitios, dos inventarios. Se elige con el pulgar, no con un desplegable. */
function DondeEstoy({
  donde,
  alCambiar,
}: {
  donde: Ubicacion;
  alCambiar: (u: Ubicacion) => void;
}): ReactNode {
  const sitios: { valor: Ubicacion; texto: string; que: string }[] = [
    { valor: 'PLANTA', texto: 'Planta', que: 'se produce' },
    { valor: 'BODEGA_BOGOTA', texto: 'Bodega Bogotá', que: 'se empaca' },
  ];

  return (
    <div className="flex gap-2">
      {sitios.map((s) => (
        <button
          key={s.valor}
          type="button"
          onClick={() => alCambiar(s.valor)}
          className={`min-h-[44px] flex-1 rounded-lg border px-3 py-1.5 text-sm font-medium ${
            donde === s.valor
              ? 'border-acento bg-acento-tenue text-acento'
              : 'border-borde bg-superficie'
          }`}
        >
          {s.texto}
          <span className="block text-xs font-normal text-tenue">{s.que}</span>
        </button>
      ))}
    </div>
  );
}

function Saldos({
  saldos,
  insumos,
}: {
  saldos: SaldoInventario[];
  insumos: InsumoDelCatalogo[];
}): ReactNode {
  if (saldos.length === 0) {
    return (
      <Card titulo="Qué hay">
        <p className="text-sm text-tenue">
          Nada todavía. <strong>Cero aquí es cero de verdad</strong>, no «no se sabe»: el
          libro está completo por construcción.
        </p>
      </Card>
    );
  }

  // Tres inventarios, tres listas. No se suman: doce kilos de cuajada, cien
  // tarrinas y cien etiquetas no son 212 de nada.
  const granel = saldos.filter((s) => s.productoId !== null);
  const empacado = saldos.filter((s) => s.presentacionId !== null);
  const deInsumo = saldos.filter((s) => s.insumoId !== null);
  const categoria = new Map(insumos.map((i) => [i.id, i.categoria]));

  return (
    <Card titulo="Qué hay">
      <Grupo titulo="Queso a granel" ayuda="Sin porcionar" saldos={granel} />
      <Grupo titulo="Empacado" ayuda="Listo para vender" saldos={empacado} />
      <Grupo
        titulo="Materia prima"
        ayuda="Lo que entra a la cuba"
        saldos={deInsumo.filter((s) => categoria.get(s.insumoId ?? '') === 'MATERIA_PRIMA')}
      />
      <Grupo
        titulo="Etiquetas"
        ayuda="Una por presentación"
        saldos={deInsumo.filter((s) => categoria.get(s.insumoId ?? '') === 'ETIQUETA')}
      />
      <Grupo
        titulo="Empaque"
        ayuda="Tarrinas, frascos, cajas"
        saldos={deInsumo.filter((s) => categoria.get(s.insumoId ?? '') === 'EMPAQUE')}
      />
    </Card>
  );
}

function Grupo({
  titulo,
  ayuda,
  saldos,
}: {
  titulo: string;
  ayuda: string;
  saldos: SaldoInventario[];
}): ReactNode {
  if (saldos.length === 0) return null;

  return (
    <div className="mt-3 first:mt-0">
      <h4 className="text-sm font-medium">
        {titulo} <span className="font-normal text-tenue">· {ayuda}</span>
      </h4>
      <ul className="mt-1.5 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {saldos.map((s) => {
          const negativo = Number(s.cantidad) < 0;
          return (
            <li
              key={`${s.productoId ?? ''}${s.presentacionId ?? ''}${s.insumoId ?? ''}`}
              className={`rounded-lg border p-3 ${
                negativo ? 'border-alerta bg-alerta-tenue' : 'border-borde bg-superficie'
              }`}
            >
              <div className="text-sm">{s.nombre}</div>
              <div className="mt-0.5 flex items-baseline gap-1.5">
                <span className={`cifra text-xl font-semibold ${negativo ? 'text-alerta' : ''}`}>
                  {formatoCantidad(s.cantidad)}
                </span>
                <span className="text-sm text-tenue">{s.unidad}</span>
              </div>
              <div className="mt-0.5 text-xs text-tenue">
                {s.movimientos} {s.movimientos === 1 ? 'movimiento' : 'movimientos'}
                {s.ultimaFecha && ` · último ${fecha(s.ultimaFecha)}`}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Lo que se puede registrar **en este sitio**.
 *
 * En planta no aparece «Empaqué» y en Bogotá no aparece «Produje queso». No es
 * esconder botones: son hechos que no ocurren ahí, y el backend los rechaza
 * igual.
 */
function Acciones({
  estado: e,
  donde,
  haciendo,
  alElegir,
  alTerminar,
}: {
  estado: EstadoInventario;
  donde: Ubicacion;
  haciendo: Accion | null;
  alElegir: (q: Accion | null) => void;
  alTerminar: () => void;
}): ReactNode {
  const botones: { clave: Accion; texto: string }[] =
    donde === 'PLANTA'
      ? [
          { clave: 'producir', texto: 'Produje queso' },
          { clave: 'enviar', texto: 'Envié a Bogotá' },
          { clave: 'insumo', texto: 'Llegó insumo' },
        ]
      : [
          { clave: 'recibir', texto: 'Recibí queso' },
          { clave: 'empacar', texto: 'Empaqué' },
          { clave: 'insumo', texto: 'Llegó insumo' },
        ];

  return (
    <Card titulo="Registrar lo que pasó">
      <div className="grid gap-2 sm:grid-cols-3">
        {botones.map((b) => (
          <button
            key={b.clave}
            type="button"
            onClick={() => alElegir(haciendo === b.clave ? null : b.clave)}
            className={`min-h-[52px] rounded-lg border px-3 text-base font-medium ${
              haciendo === b.clave
                ? 'border-acento bg-acento-tenue text-acento'
                : 'border-borde bg-superficie'
            }`}
          >
            {b.texto}
          </button>
        ))}
      </div>

      {haciendo === 'producir' && <Producir estado={e} alTerminar={alTerminar} />}
      {haciendo === 'recibir' && <Recibir estado={e} donde={donde} alTerminar={alTerminar} />}
      {haciendo === 'empacar' && <Empacar estado={e} donde={donde} alTerminar={alTerminar} />}
      {haciendo === 'enviar' && <Enviar estado={e} donde={donde} alTerminar={alTerminar} />}
      {haciendo === 'insumo' && <LlegoInsumo estado={e} donde={donde} alTerminar={alTerminar} />}
    </Card>
  );
}

/** Producir gasta lo que entró a la cuba. Solo en planta. */
function Producir({
  estado: e,
  alTerminar,
}: {
  estado: EstadoInventario;
  alTerminar: () => void;
}): ReactNode {
  const cliente = useQueryClient();
  const [quesoId, setQuesoId] = useState(e.quesos[0]?.id ?? '');
  const [cuanto, setCuanto] = useState('');
  const [gastos, setGastos] = useState<Record<string, string>>({});

  const queso = e.quesos.find((q) => q.id === quesoId);
  const materias = e.insumos.filter((i) => i.categoria === 'MATERIA_PRIMA');

  const guardar = useMutation({
    mutationFn: () =>
      producir({
        productoId: quesoId,
        cantidad: cuanto,
        unidad: queso?.unidadBase ?? 'KG',
        fecha: hoy(),
        consumos: gastosAConsumos(gastos),
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['inventario'] });
      alTerminar();
    },
  });

  return (
    <Formulario
      error={guardar.error}
      listo={quesoId !== '' && esCantidad(cuanto)}
      trabajando={guardar.isPending}
      alGuardar={() => guardar.mutate()}
    >
      <Elegir etiqueta="Qué queso" valor={quesoId} alCambiar={setQuesoId}>
        {e.quesos.map((q) => (
          <option key={q.id} value={q.id}>
            {q.nombre}
          </option>
        ))}
      </Elegir>
      <Numero
        etiqueta={`Cuánto salió (${queso?.unidadBase ?? 'KG'})`}
        valor={cuanto}
        alCambiar={setCuanto}
      />

      {materias.length > 0 && (
        <Gastos
          titulo="Qué se gastó"
          ayuda="Opcional. Ninguna receta valida la proporción: se anota lo que se gastó de verdad."
          insumos={materias}
          valores={gastos}
          alCambiar={setGastos}
        />
      )}
    </Formulario>
  );
}

/**
 * Recibir: contra un envío, o suelto si nadie anotó la salida.
 *
 * **Se recibe lo que llegó, no lo que se mandó.** Si salieron 139 kg y llegaron
 * 138, el libro dice 138 y el kilo que falta queda a la vista en el envío.
 */
function Recibir({
  estado: e,
  donde,
  alTerminar,
}: {
  estado: EstadoInventario;
  donde: Ubicacion;
  alTerminar: () => void;
}): ReactNode {
  const cliente = useQueryClient();
  const enCamino = e.enviosEnCamino.filter((v) => v.desde !== donde);
  const [envioId, setEnvioId] = useState<string>(enCamino[0]?.id ?? '');
  const [que, setQue] = useState('');
  const [cuanto, setCuanto] = useState('');

  const envio: EnvioEnCamino | undefined = enCamino.find((v) => v.id === envioId);
  const esQueso = e.quesos.some((q) => q.id === que);
  const queso = e.quesos.find((q) => q.id === que);
  const unidad = envio?.unidad ?? (esQueso ? (queso?.unidadBase ?? 'KG') : 'UN');

  const guardar = useMutation({
    mutationFn: () =>
      recibir({
        ubicacion: donde,
        envioId: envioId || null,
        // Con envío, qué llegó lo dice el envío: dejar elegir permitiría recibir
        // feta contra un envío de ibérico y nadie lo notaría.
        productoId: envioId ? null : esQueso ? que : null,
        presentacionId: envioId ? null : esQueso ? null : que || null,
        cantidad: cuanto,
        unidad,
        fecha: hoy(),
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['inventario'] });
      alTerminar();
    },
  });

  return (
    <Formulario
      error={guardar.error}
      listo={(envioId !== '' || que !== '') && esCantidad(cuanto)}
      trabajando={guardar.isPending}
      alGuardar={() => guardar.mutate()}
    >
      <Elegir etiqueta="Contra qué envío" valor={envioId} alCambiar={setEnvioId}>
        {enCamino.map((v) => (
          <option key={v.id} value={v.id}>
            {v.nombre} · {formatoCantidad(v.cantidad)} {v.unidad} · {fecha(v.fecha)}
          </option>
        ))}
        <option value="">Sin envío — nadie anotó la salida</option>
      </Elegir>

      {envioId === '' ? (
        <>
          <p className="text-xs text-tenue">
            Sin envío detrás <strong>se registra igual</strong>: el queso ya está aquí. Ese
            hueco es el dato — dice que falta anotarlo en planta.
          </p>
          <Elegir etiqueta="Qué llegó" valor={que} alCambiar={setQue}>
            <option value="">Elige…</option>
            <optgroup label="Queso a granel">
              {e.quesos.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.nombre}
                </option>
              ))}
            </optgroup>
            <optgroup label="Empacado">
              {e.presentaciones.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </optgroup>
          </Elegir>
        </>
      ) : (
        envio && (
          <p className="text-xs text-tenue">
            Salieron{' '}
            <strong>
              {formatoCantidad(envio.cantidad)} {envio.unidad}
            </strong>{' '}
            el {fecha(envio.fecha)}. <strong>Anota lo que llegó</strong>, no lo que se mandó:
            si falta algo, la diferencia es información.
          </p>
        )
      )}

      <Numero etiqueta={`Cuánto llegó (${unidad})`} valor={cuanto} alCambiar={setCuanto} />
    </Formulario>
  );
}

function Empacar({
  estado: e,
  donde,
  alTerminar,
}: {
  estado: EstadoInventario;
  donde: Ubicacion;
  alTerminar: () => void;
}): ReactNode {
  const cliente = useQueryClient();
  const [presentacionId, setPresentacionId] = useState(e.presentaciones[0]?.id ?? '');
  const [gastado, setGastado] = useState('');
  const [obtenidas, setObtenidas] = useState('');
  const [gastos, setGastos] = useState<Record<string, string>>({});

  const presentacion = e.presentaciones.find((p) => p.id === presentacionId);
  const queso = e.quesos.find((q) => q.id === presentacion?.productoId);
  const empaques = e.insumos.filter((i) => i.categoria === 'EMPAQUE');
  const etiqueta = e.insumos.find(
    (i) => i.categoria === 'ETIQUETA' && i.presentacionId === presentacionId,
  );

  const guardar = useMutation({
    mutationFn: () =>
      empacar({
        ubicacion: donde,
        productoId: presentacion!.productoId,
        presentacionId,
        granelGastado: gastado,
        unidadGranel: queso?.unidadBase ?? 'KG',
        unidadesObtenidas: obtenidas,
        fecha: hoy(),
        consumos: gastosAConsumos(gastos),
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['inventario'] });
      alTerminar();
    },
  });

  /*
   * Lo que «deberia» haberse gastado, solo para que quien lo mira lo vea.
   * **No se corrige ni se exige que cuadre**: la diferencia es la merma, y es
   * justo lo que este registro sirve para medir.
   */
  const teorico =
    presentacion && esCantidad(obtenidas)
      ? ((Number(obtenidas) * presentacion.gramos) / 1000).toFixed(3)
      : null;

  return (
    <Formulario
      error={guardar.error}
      listo={presentacionId !== '' && esCantidad(gastado) && esCantidad(obtenidas)}
      trabajando={guardar.isPending}
      alGuardar={() => guardar.mutate()}
    >
      <Elegir etiqueta="Qué presentación" valor={presentacionId} alCambiar={setPresentacionId}>
        {e.presentaciones.map((p) => (
          <option key={p.id} value={p.id}>
            {p.nombre}
          </option>
        ))}
      </Elegir>
      <Numero etiqueta="Unidades que salieron" valor={obtenidas} alCambiar={setObtenidas} />
      <Numero
        etiqueta={`Granel gastado (${queso?.unidadBase ?? 'KG'})`}
        valor={gastado}
        alCambiar={setGastado}
      />

      {teorico && (
        <p className="text-xs text-tenue">
          Esas unidades pesan <strong>{teorico} kg</strong> de queso. Si gastaste más, la
          diferencia es la merma — <strong>se registra tal cual, no se ajusta</strong>.
        </p>
      )}

      {etiqueta && esCantidad(obtenidas) && (
        <p className="text-xs text-tenue">
          Se descuentan <strong>{obtenidas}</strong> de «{etiqueta.nombre}» sin que tengas que
          decirlo: cada presentación tiene su etiqueta.
        </p>
      )}

      {empaques.length > 0 && (
        <Gastos
          titulo="Empaque gastado"
          ayuda="Opcional. La etiqueta no va aquí: se descuenta sola."
          insumos={empaques}
          valores={gastos}
          alCambiar={setGastos}
        />
      )}
    </Formulario>
  );
}

/** Enviar: sale de aquí y todavía no ha llegado a ninguna parte. */
function Enviar({
  estado: e,
  donde,
  alTerminar,
}: {
  estado: EstadoInventario;
  donde: Ubicacion;
  alTerminar: () => void;
}): ReactNode {
  const cliente = useQueryClient();
  const [que, setQue] = useState('');
  const [cuanto, setCuanto] = useState('');

  const hasta: Ubicacion = donde === 'PLANTA' ? 'BODEGA_BOGOTA' : 'PLANTA';
  const esQueso = e.quesos.some((q) => q.id === que);
  const queso = e.quesos.find((q) => q.id === que);

  const guardar = useMutation({
    mutationFn: () =>
      enviar({
        desde: donde,
        hasta,
        productoId: esQueso ? que : null,
        presentacionId: esQueso ? null : que,
        cantidad: cuanto,
        unidad: esQueso ? (queso?.unidadBase ?? 'KG') : 'UN',
        fecha: hoy(),
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['inventario'] });
      alTerminar();
    },
  });

  return (
    <Formulario
      error={guardar.error}
      listo={que !== '' && esCantidad(cuanto)}
      trabajando={guardar.isPending}
      alGuardar={() => guardar.mutate()}
    >
      <p className="text-sm">
        De <strong>{nombreDeSitio(donde)}</strong> a <strong>{nombreDeSitio(hasta)}</strong>
      </p>
      <p className="text-xs text-tenue">
        Sale de aquí y <strong>todavía no entra en ningún lado</strong>: mientras va por la
        carretera no está en ningún inventario, que es lo que pasa de verdad. Quien lo reciba
        anota la otra mitad.
      </p>
      <Elegir etiqueta="Qué" valor={que} alCambiar={setQue}>
        <option value="">Elige…</option>
        <optgroup label="Queso a granel">
          {e.quesos.map((q) => (
            <option key={q.id} value={q.id}>
              {q.nombre}
            </option>
          ))}
        </optgroup>
        <optgroup label="Empacado">
          {e.presentaciones.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
            </option>
          ))}
        </optgroup>
      </Elegir>
      <Numero
        etiqueta={`Cuánto (${esQueso ? (queso?.unidadBase ?? 'KG') : 'UN'})`}
        valor={cuanto}
        alCambiar={setCuanto}
      />
    </Formulario>
  );
}

/** Entra leche, etiquetas o empaque. Lo que no es queso también se cuenta. */
function LlegoInsumo({
  estado: e,
  donde,
  alTerminar,
}: {
  estado: EstadoInventario;
  donde: Ubicacion;
  alTerminar: () => void;
}): ReactNode {
  const cliente = useQueryClient();
  const [insumoId, setInsumoId] = useState(e.insumos[0]?.id ?? '');
  const [cuanto, setCuanto] = useState('');

  const insumo = e.insumos.find((i) => i.id === insumoId);

  const guardar = useMutation({
    mutationFn: () =>
      registrarMovimiento({
        tipo: 'ENTRADA_INSUMO',
        ubicacion: donde,
        insumoId,
        cantidad: cuanto,
        unidad: insumo?.unidad ?? 'UN',
        fecha: hoy(),
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['inventario'] });
      alTerminar();
    },
  });

  if (e.insumos.length === 0) {
    return (
      <div className="mt-3 rounded-lg border border-borde p-3 text-sm text-tenue">
        No hay insumos registrados en este sitio.
      </div>
    );
  }

  return (
    <Formulario
      error={guardar.error}
      listo={insumoId !== '' && esCantidad(cuanto)}
      trabajando={guardar.isPending}
      alGuardar={() => guardar.mutate()}
    >
      <Elegir etiqueta="Qué llegó" valor={insumoId} alCambiar={setInsumoId}>
        {e.insumos.map((i) => (
          <option key={i.id} value={i.id}>
            {i.nombre}
          </option>
        ))}
      </Elegir>
      <Numero etiqueta={`Cuánto (${insumo?.unidad ?? 'UN'})`} valor={cuanto} alCambiar={setCuanto} />
    </Formulario>
  );
}

/** Lo que se gastó en una operación. Todo opcional: en blanco es no anotarlo. */
function Gastos({
  titulo,
  ayuda,
  insumos,
  valores,
  alCambiar,
}: {
  titulo: string;
  ayuda: string;
  insumos: InsumoDelCatalogo[];
  valores: Record<string, string>;
  alCambiar: (v: Record<string, string>) => void;
}): ReactNode {
  return (
    <div className="rounded-lg border border-borde p-3">
      <h4 className="text-sm font-medium">{titulo}</h4>
      <p className="mt-0.5 text-xs text-tenue">{ayuda}</p>
      <div className="mt-2 space-y-2">
        {insumos.map((i) => (
          <label key={i.id} className="flex items-center gap-2">
            <span className="flex-1 text-sm">
              {i.nombre} <span className="text-tenue">({i.unidad})</span>
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={valores[i.id] ?? ''}
              onChange={(ev) =>
                alCambiar({ ...valores, [i.id]: ev.target.value.replace(',', '.') })
              }
              className="cifra h-12 w-28 rounded-lg border border-borde px-3 text-base"
            />
          </label>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

const TEXTO_TIPO: Record<string, string> = {
  PRODUCCION: 'Producción',
  EMPAQUE_SALIDA: 'Empaque · salió granel',
  EMPAQUE_ENTRADA: 'Empaque · entraron unidades',
  TRASLADO_SALIDA: 'Envío · salió',
  TRASLADO_ENTRADA: 'Recibido · contra su envío',
  RECEPCION_DIRECTA: 'Recibido · sin envío anotado',
  ENTRADA_INSUMO: 'Llegó insumo',
  CONSUMO_INSUMO: 'Se gastó insumo',
  DESPACHO: 'Despacho',
  DEVOLUCION: 'Devolución',
  CONTEO_INICIAL: 'Conteo inicial',
  AJUSTE: 'Ajuste',
};

/** El libro: los últimos movimientos, y de dónde sale cada saldo. */
function Libro({ movimientos }: { movimientos: MovimientoRegistrado[] }): ReactNode {
  if (movimientos.length === 0) return null;

  return (
    <Card titulo="Lo que ha pasado">
      <p className="text-sm text-tenue">
        Los últimos {movimientos.length}. <strong>Ninguno se edita ni se borra</strong>: para
        corregir se anota el contrario, con motivo.
      </p>
      <ul className="mt-3 space-y-2">
        {movimientos.map((m) => (
          <li key={m.id}>
            <FilaMovimiento movimiento={m} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function FilaMovimiento({ movimiento: m }: { movimiento: MovimientoRegistrado }): ReactNode {
  const cliente = useQueryClient();
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [motivo, setMotivo] = useState('');

  const corregir = useMutation({
    mutationFn: () => corregirMovimiento(m.id, { motivo }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['inventario'] });
      setCorrigiendo(false);
    },
  });

  const entra = Number(m.cantidad) > 0;

  return (
    <div className="rounded-lg border border-borde bg-superficie p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="text-sm font-medium">{TEXTO_TIPO[m.tipo] ?? m.tipo}</span>
        <span className={`cifra text-base font-semibold ${entra ? 'text-acento' : 'text-alerta'}`}>
          {entra ? '+' : ''}
          {formatoCantidad(m.cantidad)} {m.unidad}
        </span>
      </div>
      <div className="mt-0.5 text-sm">{m.nombre}</div>
      <div className="mt-0.5 text-xs text-tenue">
        {fecha(m.fecha)}
        {m.registradoPor && ` · ${m.registradoPor}`}
        {m.motivo && ` · ${m.motivo}`}
        {m.observaciones && ` · ${m.observaciones}`}
      </div>

      {m.corregido && (
        <div className="mt-2 rounded bg-neutro-tenue px-2 py-1 text-xs text-neutro">
          Ya corregido. <strong>Sigue aquí a propósito</strong>: pasó, y borrarlo sería
          mentir sobre lo que se supo en su momento.
        </div>
      )}

      {corregir.isError && <Error_>{(corregir.error as Error).message}</Error_>}

      {/*
        Corregir no aparece en un ajuste ni en algo ya corregido: el servidor lo
        rechaza igual, pero ofrecer un boton que no va a funcionar es peor que
        no ofrecerlo.
      */}
      {!m.corregido && m.tipo !== 'AJUSTE' && !corrigiendo && (
        <button
          type="button"
          onClick={() => setCorrigiendo(true)}
          className="mt-2 min-h-[44px] text-sm underline underline-offset-2 text-tenue"
        >
          Corregir
        </button>
      )}

      {corrigiendo && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-tenue">
            Se anota el movimiento contrario. El original se queda donde está.
          </p>
          <input
            value={motivo}
            onChange={(ev) => setMotivo(ev.target.value)}
            placeholder="Por qué se corrige"
            className="h-12 w-full rounded-lg border border-borde px-3 text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={motivo.trim().length < 3 || corregir.isPending}
              onClick={() => corregir.mutate()}
              className="min-h-[44px] flex-1 rounded-lg bg-acento px-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              Corregir
            </button>
            <button
              type="button"
              onClick={() => setCorrigiendo(false)}
              className="min-h-[44px] rounded-lg border border-borde px-3 text-sm"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Piezas del formulario. Grandes, porque se usan de pie.
// ---------------------------------------------------------------------------

function Formulario({
  children,
  error,
  listo,
  trabajando,
  alGuardar,
}: {
  children: ReactNode;
  error: unknown;
  listo: boolean;
  trabajando: boolean;
  alGuardar: () => void;
}): ReactNode {
  return (
    <div className="mt-3 space-y-3 rounded-lg border border-borde p-3">
      {children}
      {error ? <Error_>{(error as Error).message}</Error_> : null}
      <button
        type="button"
        onClick={alGuardar}
        disabled={!listo || trabajando}
        className="min-h-[52px] w-full rounded-lg bg-acento px-4 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
      >
        {trabajando ? 'Guardando…' : 'Registrar'}
      </button>
    </div>
  );
}

function Elegir({
  etiqueta,
  valor,
  alCambiar,
  children,
}: {
  etiqueta: string;
  valor: string;
  alCambiar: (v: string) => void;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="block">
      <span className="mb-1 block text-xs uppercase tracking-wide text-tenue">{etiqueta}</span>
      <select
        value={valor}
        onChange={(e) => alCambiar(e.target.value)}
        className="h-12 w-full rounded-lg border border-borde bg-superficie px-3 text-base"
      >
        {children}
      </select>
    </label>
  );
}

function Numero({
  etiqueta,
  valor,
  alCambiar,
}: {
  etiqueta: string;
  valor: string;
  alCambiar: (v: string) => void;
}): ReactNode {
  return (
    <label className="block">
      <span className="mb-1 block text-xs uppercase tracking-wide text-tenue">{etiqueta}</span>
      <input
        // `inputMode` saca el teclado numerico; `type=text` evita las flechitas
        // del navegador, que son diminutas para un dedo.
        type="text"
        inputMode="decimal"
        value={valor}
        onChange={(e) => alCambiar(e.target.value.replace(',', '.'))}
        className="cifra h-12 w-full rounded-lg border border-borde px-3 text-lg"
      />
    </label>
  );
}

/** Lo que se dejó en blanco o en cero no se anota: no pasó. */
function gastosAConsumos(gastos: Record<string, string>) {
  return Object.entries(gastos)
    .filter(([, cantidad]) => esCantidad(cantidad))
    .map(([insumoId, cantidad]) => ({ insumoId, cantidad }));
}

function nombreDeSitio(u: Ubicacion): string {
  return u === 'PLANTA' ? 'Planta' : 'Bodega Bogotá';
}

/** Hasta tres decimales y mayor que cero, igual que la base. */
function esCantidad(v: string): boolean {
  return /^\d+(\.\d{1,3})?$/.test(v) && Number(v) > 0;
}

/** Hoy en Bogotá: lo que se registra pasó hoy, no cuando el reloj del navegador diga. */
function hoy(): string {
  return new Date(Date.now() - 5 * 3600e3).toISOString().slice(0, 10);
}
